import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { format, startOfMonth, endOfMonth, eachDayOfInterval,
         isWeekend, getDay, getDaysInMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Attendance, AttendanceStatus, AttendanceJustification, VacationBalance } from '../lib/supabase';

const STATUS_LABEL: Record<string, string> = {
  present: 'Presente', late: 'Tarde', absent: 'Falta',
  vacation: 'Vacaciones', sick_leave: 'Descanso Médico', permission: 'Permiso',
};
const STATUS_BG: Record<string, string> = {
  present:    'bg-green-100 text-green-800 border-green-200',
  late:       'bg-amber-100 text-amber-800 border-amber-200',
  absent:     'bg-red-100 text-red-800 border-red-200',
  vacation:   'bg-blue-100 text-blue-800 border-blue-200',
  sick_leave: 'bg-purple-100 text-purple-800 border-purple-200',
  permission: 'bg-orange-100 text-orange-800 border-orange-200',
  none:       'bg-surface-container-low text-on-surface-variant border-outline-variant/20',
};
const JUSTIF_STATUS: Record<string, { label: string; color: string }> = {
  pending:  { label: 'Pendiente',  color: 'badge-warning' },
  approved: { label: 'Aprobada',   color: 'badge-neutral' },
  rejected: { label: 'Rechazada',  color: 'badge-error' },
};

function countWorkingDays(startStr: string, endStr: string): number {
  const start = new Date(startStr + 'T12:00:00');
  const end   = new Date(endStr   + 'T12:00:00');
  if (start > end) return 0;
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export function EmployeePortalPage() {
  const { profile, company } = useAuth();
  const [employeeId, setEmployeeId]         = useState<string | null>(null);
  const [attendance, setAttendance]         = useState<Attendance[]>([]);
  const [justifications, setJustifications] = useState<AttendanceJustification[]>([]);
  const [vacRequests, setVacRequests]       = useState<AttendanceJustification[]>([]);
  const [vacBalance, setVacBalance]         = useState<VacationBalance | null>(null);
  const [selectedDate, setSelectedDate]     = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<Attendance | null>(null);
  const [showJustifModal, setShowJustifModal] = useState(false);
  const [justifForm, setJustifForm]         = useState({ reason: '', type: 'absence' as any, vacStartDate: '', vacEndDate: '' });
  const [vacError, setVacError]             = useState<string | null>(null);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState<string | null>(null);
  const [viewMonth, setViewMonth]           = useState(new Date());

  useEffect(() => {
    if (profile) resolveEmployee();
  }, [profile]);

  useEffect(() => {
    if (employeeId) { loadAttendance(); loadJustifications(); loadVacations(); loadVacationRequests(); }
  }, [employeeId, viewMonth]);

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 3500); }

  async function resolveEmployee() {
    // Si el perfil tiene employee_id directo (portal empleado)
    if ((profile as any).employee_id) {
      setEmployeeId((profile as any).employee_id);
      return;
    }
    // Si es admin viendo su propio portal, buscar por email
    if (profile?.role === 'admin' || profile?.role === 'hr') {
      // Admins no tienen portal de empleado propio — mostrar mensaje
      setEmployeeId('__admin__');
      return;
    }
  }

  async function loadAttendance() {
    if (!employeeId || employeeId === '__admin__') return;
    const start = format(startOfMonth(viewMonth), 'yyyy-MM-dd');
    const end   = format(endOfMonth(viewMonth),   'yyyy-MM-dd');
    const { data } = await supabase
      .from('attendance')
      .select('*')
      .eq('employee_id', employeeId)
      .gte('date', start)
      .lte('date', end);
    setAttendance(data || []);
  }

  async function loadJustifications() {
    if (!employeeId || employeeId === '__admin__') return;
    const start = format(startOfMonth(viewMonth), 'yyyy-MM-dd');
    const end   = format(endOfMonth(viewMonth),   'yyyy-MM-dd');
    const { data } = await supabase
      .from('attendance_justifications')
      .select('*')
      .eq('employee_id', employeeId)
      .gte('date', start)
      .lte('date', end)
      .order('created_at', { ascending: false });
    setJustifications(data || []);
  }

  async function loadVacations() {
    if (!employeeId || employeeId === '__admin__') return;
    const year = viewMonth.getFullYear();
    const { data } = await supabase
      .from('vacation_balances')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('year', year)
      .single();
    setVacBalance(data);
  }

  async function loadVacationRequests() {
    if (!employeeId || employeeId === '__admin__') return;
    const { data } = await supabase
      .from('attendance_justifications')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('type', 'vacation_request')
      .order('created_at', { ascending: false })
      .limit(20);
    setVacRequests(data || []);
  }

  function getRecordForDate(dateStr: string): Attendance | undefined {
    return attendance.find(a => a.date === dateStr);
  }

  function getJustifForDate(dateStr: string): AttendanceJustification | undefined {
    return justifications.find(j => j.date === dateStr);
  }

  function handleDayClick(dateStr: string) {
    const record = getRecordForDate(dateStr);
    const today = format(new Date(), 'yyyy-MM-dd');
    // Solo se puede justificar días pasados o hoy
    if (dateStr > today) return;
    setSelectedDate(dateStr);
    setSelectedRecord(record || null);
    // Pre-seleccionar tipo según estado
    const type = record?.status === 'late' ? 'tardiness'
               : record?.status === 'absent' ? 'absence'
               : 'absence';
    setJustifForm({ reason: '', type, vacStartDate: '', vacEndDate: '' });
    setShowJustifModal(true);
  }

  async function submitJustification() {
    if (!employeeId) return;

    // ── Ruta solicitud de vacaciones ─────────────────────────────────────────
    if (justifForm.type === 'vacation_request') {
      const { vacStartDate, vacEndDate } = justifForm;
      if (!vacStartDate || !vacEndDate) return;
      if (vacEndDate < vacStartDate) {
        setVacError('La fecha de fin debe ser posterior a la fecha de inicio.');
        return;
      }

      const requestedDays = countWorkingDays(vacStartDate, vacEndDate);
      if (requestedDays === 0) {
        setVacError('El rango seleccionado no incluye días hábiles (lunes a viernes).');
        return;
      }

      const entitledDays = company?.regime === 'general' ? 30 : 15;
      const available = vacBalance
        ? vacBalance.days_entitled - vacBalance.days_taken - vacBalance.days_pending
        : entitledDays;

      if (requestedDays > available) {
        setVacError(`No tienes suficientes días disponibles. Solicitados: ${requestedDays}, disponibles: ${available}.`);
        return;
      }

      setVacError(null);
      setSaving(true);

      const label    = requestedDays === 1 ? '1 día hábil' : `${requestedDays} días hábiles`;
      const dateText = `Fechas: ${vacStartDate} al ${vacEndDate} (${label})`;
      const reasonText = justifForm.reason.trim()
        ? `${dateText} — ${justifForm.reason.trim()}`
        : dateText;

      const { error } = await supabase.from('attendance_justifications').insert({
        company_id:           company!.id,
        employee_id:          employeeId,
        attendance_id:        null,
        date:                 vacStartDate,
        type:                 'vacation_request',
        reason:               reasonText,
        status:               'pending',
        vacation_start_date:  vacStartDate,
        vacation_end_date:    vacEndDate,
        vacation_days_count:  requestedDays,
      });

      if (error) { setSaving(false); showToast('Error al enviar la solicitud'); return; }

      // Actualizar o crear saldo de vacaciones
      const year = new Date(vacStartDate).getFullYear();
      if (vacBalance && vacBalance.year === year) {
        await supabase.from('vacation_balances').update({
          days_pending: vacBalance.days_pending + requestedDays,
        }).eq('id', vacBalance.id);
      } else {
        await supabase.from('vacation_balances').insert({
          company_id:    company!.id,
          employee_id:   employeeId,
          year,
          days_entitled: entitledDays,
          days_taken:    0,
          days_pending:  requestedDays,
        });
      }

      setSaving(false);
      showToast(`Solicitud enviada — ${label} pendiente${requestedDays !== 1 ? 's' : ''} de aprobación`);
      setShowJustifModal(false);
      loadVacations();
      loadVacationRequests();
      return;
    }

    // ── Ruta justificación de falta / tardanza ───────────────────────────────
    if (!justifForm.reason.trim() || !selectedDate) return;
    setSaving(true);
    const record = getRecordForDate(selectedDate);
    const { error } = await supabase.from('attendance_justifications').insert({
      company_id:    company!.id,
      employee_id:   employeeId,
      attendance_id: record?.id || null,
      date:          selectedDate,
      type:          justifForm.type,
      reason:        justifForm.reason.trim(),
      status:        'pending',
    });
    setSaving(false);
    if (error) { showToast('Error al enviar justificación'); return; }
    showToast('Justificación enviada — el administrador la revisará');
    setShowJustifModal(false);
    loadJustifications();
  }

  // Calendario
  const days = useMemo(() => {
    const start = startOfMonth(viewMonth);
    const end   = endOfMonth(viewMonth);
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  // Primer día de la semana del mes (0=Dom…6=Sab → ajustar a lunes=0)
  const firstDayOfWeek = (getDay(startOfMonth(viewMonth)) + 6) % 7;

  const stats = useMemo(() => {
    const present  = attendance.filter(a => a.status === 'present').length;
    const late     = attendance.filter(a => a.status === 'late').length;
    const absent   = attendance.filter(a => a.status === 'absent').length;
    const vacation = attendance.filter(a => a.status === 'vacation').length;
    const lateMin  = attendance.reduce((s, a) => s + (a.late_minutes || 0), 0);
    return { present, late, absent, vacation, lateMin };
  }, [attendance]);

  if (employeeId === '__admin__') {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="card p-10 text-center">
          <span className="material-symbols-outlined text-4xl text-outline-variant mb-3">account_circle</span>
          <h2 className="text-xl font-bold font-headline mb-2">Portal de Empleado</h2>
          <p className="text-on-surface-variant text-sm">
            Tu cuenta es de administrador. El portal de empleado está disponible para usuarios con rol <strong>Colaborador</strong>.
          </p>
          <p className="text-xs text-on-surface-variant mt-3">
            Para crear un acceso de empleado: ve a <strong>Empleados</strong> → edita el empleado → asígnale un email → luego créale una cuenta en Supabase Auth con ese email y role "employee".
          </p>
        </div>
      </div>
    );
  }

  if (!employeeId) return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">check_circle</span>{toast}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-on-surface font-headline">Mi Asistencia</h1>
        <p className="text-on-surface-variant mt-1 text-sm">Haz clic en cualquier día para justificar una falta o tardanza</p>
      </div>

      {/* Stats del mes */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Presentes',  value: stats.present,  color: 'text-green-600',  bg: 'bg-green-50',  icon: 'check_circle' },
          { label: 'Tardanzas',  value: stats.late,     color: 'text-amber-600',  bg: 'bg-amber-50',  icon: 'schedule' },
          { label: 'Faltas',     value: stats.absent,   color: 'text-error',      bg: 'bg-red-50',    icon: 'cancel' },
          { label: 'Vacaciones', value: stats.vacation, color: 'text-blue-600',   bg: 'bg-blue-50',   icon: 'beach_access' },
          { label: 'Min. tarde', value: stats.lateMin,  color: 'text-secondary',  bg: 'bg-surface-container-low', icon: 'timer' },
        ].map(s => (
          <div key={s.label} className={`card p-4 ${s.bg}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{s.label}</span>
              <span className={`material-symbols-outlined ${s.color} text-[18px]`}>{s.icon}</span>
            </div>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Vacaciones */}
      {vacBalance && (
        <div className="card p-5 flex items-center gap-5 bg-blue-50 border border-blue-200">
          <span className="material-symbols-outlined text-blue-600 text-3xl">beach_access</span>
          <div className="flex-1">
            <p className="font-bold text-on-surface">Saldo de Vacaciones {viewMonth.getFullYear()}</p>
            <div className="flex gap-6 mt-1 text-sm">
              <span className="text-on-surface-variant">Disponibles: <strong className="text-green-700">{vacBalance.days_entitled - vacBalance.days_taken - vacBalance.days_pending} días</strong></span>
              <span className="text-on-surface-variant">Tomados: <strong>{vacBalance.days_taken}</strong></span>
              <span className="text-on-surface-variant">Pendientes: <strong className="text-amber-600">{vacBalance.days_pending}</strong></span>
              <span className="text-on-surface-variant">Total derecho: <strong>{vacBalance.days_entitled}</strong></span>
            </div>
          </div>
          <button
            onClick={() => { setJustifForm({ reason: '', type: 'vacation_request', vacStartDate: '', vacEndDate: '' }); setSelectedDate(format(new Date(), 'yyyy-MM-dd')); setShowJustifModal(true); }}
            className="btn-primary text-white px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap"
          >
            Solicitar días
          </button>
        </div>
      )}

      {/* Calendario */}
      <div className="card overflow-hidden">
        {/* Nav mes */}
        <div className="p-4 border-b border-outline-variant/10 flex items-center justify-between">
          <button
            onClick={() => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() - 1))}
            className="p-2 hover:bg-surface-container-high rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">chevron_left</span>
          </button>
          <h3 className="font-bold text-on-surface font-headline capitalize">
            {format(viewMonth, 'MMMM yyyy', { locale: es })}
          </h3>
          <button
            onClick={() => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + 1))}
            className="p-2 hover:bg-surface-container-high rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">chevron_right</span>
          </button>
        </div>

        {/* Días de semana */}
        <div className="grid grid-cols-7 bg-surface-container-low">
          {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">
              {d}
            </div>
          ))}
        </div>

        {/* Celdas */}
        <div className="grid grid-cols-7 gap-px bg-outline-variant/10">
          {/* celdas vacías al inicio */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-surface-container-lowest h-16" />
          ))}

          {days.map(day => {
            const dateStr  = format(day, 'yyyy-MM-dd');
            const record   = getRecordForDate(dateStr);
            const justif   = getJustifForDate(dateStr);
            const isToday  = dateStr === format(new Date(), 'yyyy-MM-dd');
            const isFuture = dateStr > format(new Date(), 'yyyy-MM-dd');
            const isWknd   = isWeekend(day);
            const statusKey = record?.status || 'none';

            return (
              <div
                key={dateStr}
                onClick={() => !isFuture && !isWknd && handleDayClick(dateStr)}
                className={`bg-surface-container-lowest h-16 p-1.5 flex flex-col relative
                  ${!isFuture && !isWknd ? 'cursor-pointer hover:bg-surface-container-low/50' : ''}
                  ${isToday ? 'ring-2 ring-primary ring-inset' : ''}
                  ${isWknd ? 'bg-surface-container-low/30 opacity-60' : ''}
                  transition-colors`}
              >
                {/* Número del día */}
                <span className={`text-xs font-bold self-start leading-none ${
                  isToday ? 'text-primary' : 'text-on-surface-variant'
                }`}>
                  {format(day, 'd')}
                </span>

                {/* Estado */}
                {record && (
                  <span className={`mt-auto text-[9px] font-bold px-1 py-0.5 rounded border text-center truncate ${STATUS_BG[statusKey]}`}>
                    {STATUS_LABEL[statusKey]}
                  </span>
                )}

                {/* Indicador tardanza */}
                {record?.late_minutes ? (
                  <span className="absolute top-0.5 right-0.5 text-[8px] text-amber-600 font-bold">
                    +{record.late_minutes}'
                  </span>
                ) : null}

                {/* Indicador justificación */}
                {justif && (
                  <span className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${
                    justif.status === 'approved' ? 'bg-green-500' :
                    justif.status === 'rejected' ? 'bg-error' : 'bg-amber-500'
                  }`} title={`Justificación ${JUSTIF_STATUS[justif.status].label}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="p-3 border-t border-outline-variant/10 flex flex-wrap gap-3">
          {[
            { key: 'present',    label: 'Presente' },
            { key: 'late',       label: 'Tardanza' },
            { key: 'absent',     label: 'Falta' },
            { key: 'vacation',   label: 'Vacación' },
            { key: 'sick_leave', label: 'Descanso' },
          ].map(s => (
            <span key={s.key} className={`text-[10px] font-bold px-2 py-0.5 rounded border ${STATUS_BG[s.key]}`}>
              {s.label}
            </span>
          ))}
          <span className="text-[10px] text-on-surface-variant flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Justif. pendiente
          </span>
          <span className="text-[10px] text-on-surface-variant flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Justif. aprobada
          </span>
        </div>
      </div>

      {/* Solicitudes de vacaciones */}
      {vacRequests.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-outline-variant/10 flex items-center gap-2">
            <span className="material-symbols-outlined text-blue-600 text-[20px]">beach_access</span>
            <h3 className="font-bold text-on-surface font-headline text-sm">Mis solicitudes de vacaciones</h3>
          </div>
          <div className="divide-y divide-outline-variant/10">
            {vacRequests.map(v => {
              const daysCount = v.vacation_days_count ?? 1;
              const startDate = v.vacation_start_date ?? v.date;
              const endDate   = v.vacation_end_date   ?? v.date;
              return (
                <div key={v.id} className="p-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-on-surface">
                        {startDate}{endDate !== startDate ? ` al ${endDate}` : ''}
                      </span>
                      <span className="text-xs bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">
                        {daysCount} día{daysCount !== 1 ? 's' : ''} hábil{daysCount !== 1 ? 'es' : ''}
                      </span>
                      <span className={JUSTIF_STATUS[v.status].color}>{JUSTIF_STATUS[v.status].label}</span>
                    </div>
                    {v.review_note && (
                      <p className="text-xs text-primary italic">Nota del admin: {v.review_note}</p>
                    )}
                  </div>
                  <span className={`material-symbols-outlined text-[22px] ${
                    v.status === 'approved' ? 'text-green-500' :
                    v.status === 'rejected' ? 'text-error' : 'text-amber-500'
                  }`}>
                    {v.status === 'approved' ? 'check_circle' : v.status === 'rejected' ? 'cancel' : 'pending'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Historial de justificaciones del mes (faltas/tardanzas) */}
      {justifications.filter(j => j.type !== 'vacation_request').length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-outline-variant/10">
            <h3 className="font-bold text-on-surface font-headline text-sm">Mis justificaciones del mes</h3>
          </div>
          <div className="divide-y divide-outline-variant/10">
            {justifications.filter(j => j.type !== 'vacation_request').map(j => (
              <div key={j.id} className="p-4 flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-on-surface">{j.date}</span>
                    <span className="text-xs text-on-surface-variant">
                      {j.type === 'absence' ? 'Falta' : 'Tardanza'}
                    </span>
                    <span className={JUSTIF_STATUS[j.status].color}>{JUSTIF_STATUS[j.status].label}</span>
                  </div>
                  <p className="text-sm text-on-surface-variant">{j.reason}</p>
                  {j.review_note && (
                    <p className="text-xs text-primary mt-1 italic">Nota del admin: {j.review_note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal justificación */}
      {showJustifModal && selectedDate && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-outline-variant/10">
              <div>
                <h2 className="text-lg font-bold font-headline">
                  {justifForm.type === 'vacation_request' ? 'Solicitud de vacaciones' : 'Justificar inasistencia'}
                </h2>
                <p className="text-xs text-on-surface-variant mt-0.5">{selectedDate}</p>
              </div>
              <button onClick={() => setShowJustifModal(false)} className="p-2 hover:bg-surface-container-high rounded-lg">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Tipo */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Tipo</label>
                <select
                  className="input-base"
                  value={justifForm.type}
                  onChange={e => setJustifForm(p => ({ ...p, type: e.target.value as any }))}
                >
                  <option value="absence">Falta justificada</option>
                  <option value="tardiness">Tardanza justificada</option>
                  <option value="vacation_request">Solicitud de vacaciones</option>
                </select>
              </div>

              {/* Fechas y detalle para solicitud de vacaciones */}
              {justifForm.type === 'vacation_request' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                        Fecha inicio *
                      </label>
                      <input
                        type="date"
                        className="input-base"
                        value={justifForm.vacStartDate}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        onChange={e => { setVacError(null); setJustifForm(p => ({ ...p, vacStartDate: e.target.value })); }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                        Fecha fin *
                      </label>
                      <input
                        type="date"
                        className="input-base"
                        value={justifForm.vacEndDate}
                        min={justifForm.vacStartDate || format(new Date(), 'yyyy-MM-dd')}
                        onChange={e => { setVacError(null); setJustifForm(p => ({ ...p, vacEndDate: e.target.value })); }}
                      />
                    </div>
                  </div>

                  {/* Resumen de días */}
                  {justifForm.vacStartDate && justifForm.vacEndDate && justifForm.vacEndDate >= justifForm.vacStartDate && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                      <span className="text-blue-800">
                        Días hábiles solicitados: <strong className="text-blue-900">
                          {countWorkingDays(justifForm.vacStartDate, justifForm.vacEndDate)}
                        </strong>
                      </span>
                      {vacBalance && (
                        <span className="text-blue-800">
                          Disponibles: <strong className={
                            countWorkingDays(justifForm.vacStartDate, justifForm.vacEndDate) >
                            (vacBalance.days_entitled - vacBalance.days_taken - vacBalance.days_pending)
                              ? 'text-red-600' : 'text-green-700'
                          }>
                            {vacBalance.days_entitled - vacBalance.days_taken - vacBalance.days_pending}
                          </strong>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Error de validación */}
                  {vacError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                      <span className="material-symbols-outlined text-[16px] mt-0.5 flex-shrink-0">error</span>
                      {vacError}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                      Comentario adicional (opcional)
                    </label>
                    <textarea
                      className="input-base h-20 resize-none"
                      placeholder="Ej: Viaje familiar, descanso programado..."
                      value={justifForm.reason}
                      onChange={e => setJustifForm(p => ({ ...p, reason: e.target.value }))}
                    />
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800">
                    Al ser aprobada, los días se marcarán automáticamente como <strong>Vacaciones</strong> en tu registro de asistencia.
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                      Motivo / Descripción *
                    </label>
                    <textarea
                      className="input-base h-24 resize-none"
                      placeholder="Explica el motivo de la falta o tardanza (enfermedad, emergencia, etc.)..."
                      value={justifForm.reason}
                      onChange={e => setJustifForm(p => ({ ...p, reason: e.target.value }))}
                    />
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                    <strong>ℹ️</strong> Si es aprobada, la falta/tardanza no se descontará de tu planilla.
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-outline-variant/10">
              <button onClick={() => setShowJustifModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">
                Cancelar
              </button>
              <button
                onClick={submitJustification}
                disabled={saving || (
                  justifForm.type === 'vacation_request'
                    ? !justifForm.vacStartDate || !justifForm.vacEndDate || justifForm.vacEndDate < justifForm.vacStartDate
                    : !justifForm.reason.trim()
                )}
                className="flex-1 btn-primary text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-50"
              >
                {saving ? 'Enviando...' : 'Enviar justificación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
